import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Select from 'react-select';
import { XIcon } from '@heroicons/react/outline';
import {
  groupedExpressionFieldOptions,
  allGroupedExpressionFieldOptions,
  operatorOptions,
  getDrownDownOptionByFieldName,
  getFieldCategory,
  getStatementInputConfig,
} from '../../containers/NestedContainers/ClaimAutomation/utils';
import { MultiSelect } from 'components/MultiSelect';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';

interface VisualRuleConditionCardProps {
  id: string;
  index: number;
  statement: any;
  onChange: (changes: Record<string, any>) => void;
  onRemove: () => void;
  canRemove: boolean;
  disabled?: boolean;
}

export const VisualRuleConditionCard: React.FC<
  VisualRuleConditionCardProps
> = ({
  id,
  index,
  statement,
  onChange,
  onRemove,
  canRemove,
  disabled = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const category = getFieldCategory(statement.expressionField);

  const getFilteredOperatorOptions = () => {
    if (statement.expressionField) {
      const fieldDef = allGroupedExpressionFieldOptions.find(
        (f) => f.value === statement.expressionField,
      );
      if (fieldDef?.type) {
        return operatorOptions.filter((o) => o.type === fieldDef.type);
      }
    }
    return operatorOptions;
  };

  const handleFieldChange = (selected: any) => {
    if (disabled) return;
    const newField = selected?.value || '';
    const fieldDef = allGroupedExpressionFieldOptions.find(
      (f) => f.value === newField,
    );
    const validOperators = operatorOptions.filter(
      (o) => o.type === fieldDef?.type,
    );
    const defaultOperator = validOperators[0]?.value || '';
    const config = getStatementInputConfig(newField, defaultOperator);

    onChange({
      expressionField: newField,
      expression: defaultOperator,
      expressionValue: '',
      config,
    });
  };

  const handleOperatorChange = (selected: any) => {
    if (disabled) return;
    const newOp = selected?.value || '';
    const config = getStatementInputConfig(statement.expressionField, newOp);
    onChange({ expression: newOp, expressionValue: '', config });
  };

  const handleValueChange = (val: any) => {
    if (disabled) return;
    onChange({ expressionValue: val });
  };

  const isCurrencyField = [
    'orderValue',
    'productPrice',
    'orderDiscount',
  ].includes(statement.expressionField);

  const renderValueInput = () => {
    const config = statement.config || {};
    if (config.field === 'stringDropDown') {
      const dropdownOptions = getDrownDownOptionByFieldName(
        statement.expressionField,
      );
      const isMultiAllowed = !['_eq', '_neq'].includes(statement.expression);
      const isArrayVal = Array.isArray(statement.expressionValue);
      const selectedValues = isArrayVal
        ? (statement.expressionValue as any[])
            .map((s) => String(s).trim())
            .filter(Boolean)
        : statement.expressionValue !== null &&
          statement.expressionValue !== undefined
        ? String(statement.expressionValue)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [];

      const options = [
        ...dropdownOptions.map((o: any) => ({
          label: o.label || o.value,
          val: o.value || o.label,
        })),
      ];
      selectedValues.forEach((val) => {
        if (!options.some((opt) => opt.val === val)) {
          options.push({ label: val, val });
        }
      });

      return (
        <div className="w-full min-w-0">
          <MultiSelect
            options={options}
            selected={selectedValues}
            setSelected={(vals: (string | number)[]) => {
              if (disabled) return;
              const stringVals = vals.map(String);
              handleValueChange(
                isArrayVal ? stringVals : stringVals.join(','),
              );
            }}
            placeholder={config.placeHolder || 'Select option'}
            style={{ minHeight: 38 }}
            maxValuesAllowed={isMultiAllowed ? undefined : 1}
            disabled={disabled}
          />
        </div>
      );
    }

    if (config.field === 'numberInput') {
      return (
        <div className="relative rounded-md shadow-sm w-full min-w-0">
          {isCurrencyField && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm font-medium">$</span>
            </div>
          )}
          <NonNegativeNumberInput
            aria-label={`Condition ${index + 1} expected value`}
            step="any"
            min="0"
            disabled={disabled}
            className={`block w-full rounded-md border border-gray-300 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed ${
              isCurrencyField ? 'pl-7 pr-3' : 'px-3'
            }`}
            placeholder={config.placeHolder || '0.00'}
            value={
              statement.expressionValue !== undefined &&
              statement.expressionValue !== null
                ? statement.expressionValue
                : ''
            }
            onChange={(e) => handleValueChange(e.target.value)}
          />
        </div>
      );
    }

    const textValue =
      statement.expressionValue !== undefined &&
      statement.expressionValue !== null
        ? String(statement.expressionValue)
        : '';

    return (
      <div className="w-full min-w-0">
        <input
          aria-label={`Condition ${index + 1} expected value`}
          type="text"
          disabled={disabled}
          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed"
          placeholder={config.placeHolder || 'Enter value...'}
          value={textValue}
          onChange={(e) => handleValueChange(e.target.value)}
        />
      </div>
    );
  };

  const selectedFieldOption =
    allGroupedExpressionFieldOptions.find(
      (f) => f.value === statement.expressionField,
    ) ||
    (statement.expressionField
      ? { value: statement.expressionField, label: statement.expressionField }
      : null);

  const currentOperators = getFilteredOperatorOptions();
  const selectedOperatorOption =
    currentOperators.find((o) => o.value === statement.expression) || null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-xl border border-slate-200/90 shadow-sm p-4 hover:border-slate-300 transition-all duration-150"
    >
      <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          {/* Drag Handle */}
          <button
            type="button"
            disabled={disabled}
            {...(disabled ? {} : attributes)}
            {...(disabled ? {} : listeners)}
            className={`p-1 rounded ${
              disabled
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing hover:bg-slate-100'
            } transition-colors`}
            title={disabled ? undefined : 'Drag to reorder condition'}
            aria-label="Drag condition"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-12a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
            </svg>
          </button>

          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Condition {index + 1}
          </span>

          {statement.expressionField && (
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded-full border ${category.color}`}
            >
              {category.label}
            </span>
          )}
        </div>

        {canRemove && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              onRemove();
            }}
            className={`p-1 rounded-md transition-colors ${
              disabled
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-400 hover:text-red-500 hover:bg-red-50'
            }`}
            title={disabled ? undefined : 'Remove condition'}
            aria-label="Remove condition"
          >
            <XIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
        {/* Field Selector */}
        <div className="sm:col-span-5">
          <label className="block text-xs font-medium text-slate-600 mb-1">
            When field
          </label>
          <Select
            aria-label={`Condition ${index + 1} field`}
            value={selectedFieldOption}
            onChange={handleFieldChange}
            options={groupedExpressionFieldOptions}
            placeholder="Select a field..."
            isDisabled={disabled}
            className="text-sm"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '38px',
                borderColor: '#cbd5e1',
                borderRadius: '0.375rem',
              }),
            }}
          />
        </div>

        {/* Operator Selector */}
        <div className="sm:col-span-3">
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Operator
          </label>
          <Select
            aria-label={`Condition ${index + 1} operator`}
            value={selectedOperatorOption}
            onChange={handleOperatorChange}
            options={currentOperators}
            placeholder="Operator"
            isDisabled={disabled || !statement.expressionField}
            className="text-sm"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '38px',
                borderColor: '#cbd5e1',
                borderRadius: '0.375rem',
              }),
            }}
          />
        </div>

        {/* Value Input */}
        <div className="sm:col-span-4">
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Expected value
          </label>
          {renderValueInput()}
        </div>
      </div>
    </div>
  );
};
